import { useEffect, useRef, useState } from 'react';
import { CheckCircleRounded, ErrorRounded, HourglassBottomRounded } from '@mui/icons-material';
import { IDocument, DocumentStatus } from './types';

interface Props {
  document: IDocument;
  onReady: (doc: IDocument) => void;
  onError: (doc: IDocument) => void;
  getStatus: (docId: string) => Promise<IDocument>;
}

const STATUS_LABELS: Record<DocumentStatus, string> = {
  uploading: 'Uploading…',
  pending: 'Queued',
  processing: 'Processing',
  parsing: 'Extracting text',
  embedding: 'Generating embeddings',
  indexing: 'Indexing',
  ready: 'Ready',
  failed: 'Failed',
};

const ACTIVE_STATUSES: DocumentStatus[] = ['pending', 'processing', 'parsing', 'embedding', 'indexing'];

const DocStatus = ({ document, onReady, onError, getStatus }: Props) => {
  const [doc, setDoc] = useState<IDocument>(document);
  const [pollCount, setPollCount] = useState(0);
  const mountedRef = useRef(true);
  const pollDocIdRef = useRef(doc.doc_id);
  pollDocIdRef.current = doc.doc_id;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!ACTIVE_STATUSES.includes(doc.status)) return;

    const interval = setInterval(async () => {
      try {
        const updated = await getStatus(doc.doc_id);
        if (!mountedRef.current || updated.doc_id !== pollDocIdRef.current) return;
        setDoc(updated);
        setPollCount((c) => c + 1);

        if (updated.status === 'ready') {
          clearInterval(interval);
          onReady(updated);
        } else if (updated.status === 'failed') {
          clearInterval(interval);
          onError(updated);
        }
      } catch {
        setPollCount((c) => c + 1);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [doc.doc_id, doc.status]);

  const isActive = ACTIVE_STATUSES.includes(doc.status);
  const isReady = doc.status === 'ready';
  const isFailed = doc.status === 'failed';

  return (
    <div className='chatBot--doc-status-card'>
      <div className='chatBot--doc-status-head'>
        {isReady && <CheckCircleRounded sx={{ fontSize: 18, color: '#2EB273' }} />}
        {isFailed && <ErrorRounded sx={{ fontSize: 18, color: '#C62828' }} />}
        {isActive && <HourglassBottomRounded sx={{ fontSize: 18, color: '#2EB273' }} className='chatBot--doc-status-spin' />}
        <div className='chatBot--doc-status-info'>
          <span className='chatBot--doc-status-name'>{doc.filename}</span>
          <span className='chatBot--doc-status-label'>
            {STATUS_LABELS[doc.status]}
            {doc.pages ? ` · ${doc.pages} pages` : ''}
            {doc.category ? ` · ${doc.category}` : ''}
          </span>
        </div>
      </div>

      {isActive && (
        <div className='chatBot--doc-progress-bar'>
          <div className='chatBot--doc-progress-fill chatBot--doc-progress-fill--indeterminate' />
        </div>
      )}

      {isFailed && (
        <div className='chatBot--doc-status-error'>
          {doc.error || 'Document processing failed'}
          {doc.error_code && <span className='chatBot--doc-status-error-code'>{doc.error_code}</span>}
        </div>
      )}

      {isReady && (
        <div className='chatBot--doc-status-ready'>
          {doc.chunk_count} chunks indexed · Ready to chat
        </div>
      )}
    </div>
  );
};

export default DocStatus;
